import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";

export function TestimonialsSection() {
  const { language } = useLanguage();

  const content = {
    fr: {
      title: "Ils nous font confiance",
      subtitle: "Découvrez comment nos clients ont boosté leur réputation",
      testimonials: [
        {
          name: "Dr. Laurent",
          role: "Médecin généraliste",
          quote: "Depuis OuraTrust, notre cabinet a vu sa note passer de 3.8 à 4.7 en 3 mois. Nous résolvons les insatisfactions clients avant même qu'elles ne deviennent un problème.",
          avatar: "L",
          rating: 4.7,
        },
        {
          name: "Sophie",
          role: "Gérante de boutique",
          quote: "On collectait 2-3 avis par mois avant. Maintenant c'est 15-20 avis positifs ! Le QR Code au comptoir a tout changé. L'investissement est rentabilisé en une semaine.",
          avatar: "S",
          rating: 4.8,
        },
        {
          name: "Marie",
          role: "Directrice de salon de coiffure",
          quote: "L'assistant IA me fait gagner un temps fou pour répondre aux avis. Et le dashboard me permet de suivre notre progression en temps réel. Indispensable pour mon commerce !",
          avatar: "M",
          rating: 4.6,
        },
      ],
    },
    en: {
      title: "Trusted by professionals worldwide",
      subtitle: "See how business owners across the globe boosted their Google ratings",
      testimonials: [
        {
          name: "Dr. Michael Chen",
          role: "Family Dentist, Chicago IL",
          quote: "In healthcare, a single unfair review can be devastating. OuraTrust gave us the tools to protect our reputation and focus on our patients. The AI responses are surprisingly professional and spot-on.",
          avatar: "MC",
          rating: 4.9,
        },
        {
          name: "Robert 'Bobby' Sullivan",
          role: "Owner, Sully's Auto Repair, Dublin",
          quote: "We're great at fixing cars, not at writing Google replies. OuraTrust handles our reviews while we're under the hood. Our rating went from 3.8 to 4.7 in less than two months. Life-changer.",
          avatar: "RS",
          rating: 4.7,
        },
        {
          name: "Sarah Jenkins",
          role: "Boutique Owner, London (Soho)",
          quote: "The QR code system is brilliant. Our customers actually find it fun to leave a review now. We've seen a massive spike in foot traffic since we hit the Google Maps Top 3.",
          avatar: "SJ",
          rating: 4.8,
        },
      ],
    },
  };

  const t = content[language];

  return (
    <section className="py-24 bg-gradient-to-b from-white to-slate-50">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            {t.title}
          </h2>
          <p className="text-lg text-muted-foreground">{t.subtitle}</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {t.testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15 }}
              className="relative bg-white rounded-2xl p-8 shadow-lg border border-slate-100 hover:shadow-xl transition-shadow"
            >
              {/* Quote icon */}
              <div className="absolute -top-4 right-6">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Quote className="text-primary" size={20} />
                </div>
              </div>

              {/* Rating badge */}
              <div className="flex items-center gap-1 mb-6">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    size={16}
                    className={i < Math.floor(testimonial.rating) ? "text-warning fill-warning" : "text-slate-200"}
                  />
                ))}
                <span className="ml-2 text-sm font-semibold text-foreground">
                  {testimonial.rating}
                </span>
              </div>

              {/* Quote */}
              <blockquote className="text-muted-foreground mb-6 leading-relaxed">
                "{testimonial.quote}"
              </blockquote>

              {/* Author */}
              <div className="flex items-center gap-4 pt-4 border-t border-slate-100">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white font-semibold">
                  {testimonial.avatar}
                </div>
                <div>
                  <p className="font-semibold text-foreground">{testimonial.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {testimonial.role}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
